import { SentryError } from '@ifixit/sentry';
import type { EditorPreset, TiptapExtension } from '.';
import { CommentsExtensions } from './comments';
import { GuideIntroExtensions } from './guide_intro';
import { AnswersExtensions } from './answers';
import { WikiExtensions } from './wiki';
import {
   CustomCharacterCount,
   IsFocused,
   SessionStorage,
   OriginalState,
   SubmitError,
   Submit,
   ValidateDoc,
   Editorid,
} from '../extensions';
import { CharacterCountConfig } from '../extensions/custom_character_count';
import * as Sentry from '@sentry/browser';

export const parseEditorContent = (text: string) => {
   if (!text) {
      return '';
   }
   try {
      return JSON.parse(text);
   } catch {
      return text;
   }
};

export const getExtensionsForPreset = (preset: EditorPreset): Array<TiptapExtension> => {
   switch (preset) {
      case 'comments': {
         return CommentsExtensions;
      }
      case 'guide_intro': {
         return GuideIntroExtensions;
      }
      case 'answers': {
         return AnswersExtensions;
      }
      case 'wiki': {
         return WikiExtensions;
      }
      default: {
         throw new SentryError(`Invalid preset: ${preset}`);
      }
   }
};

const fallbackToPlaintext = (err: any) => {
   const isPlaintext = new URL(document.location.href).searchParams.get('plaintext');
   if (isPlaintext) {
      // If we're already in plaintext mode, don't redirect again.
      return;
   }
   if (err) {
      // Log the error so we can see why the page is redirecting
      console.error(err);

      // Capture the error in Sentry. This gives us additional
      // awareness of what's breaking (since it's likely our fault).
      Sentry.captureException(err);
   }

   // Redirect page once error-monitoring code has had a chance to
   // deal with the thrown exception below.
   const url = new URL(document.location.href);
   url.searchParams.append('plaintext', '1');
   window.location.href = url.href;
};

export const getDefaultExtensions = (props: {
   editorid: string;
   text?: string;
   characterCountConfig?: CharacterCountConfig;
   submitHandler?: () => void;
   handleModified?: (modified: boolean) => void;
}) => {
   const {
      editorid,
      text = '',
      characterCountConfig,
      submitHandler,
      handleModified = () => {},
   } = props;

   const extensions: TiptapExtension[] = [
      IsFocused,
      // onCreate runs in array order: the clean baseline must be the loaded
      // content, taken before SessionStorage restores a draft over it.
      OriginalState.configure({
         handleModified,
      }),
      SessionStorage.configure({
         editorId: editorid,
         autosaveWait: 200,
      }),
      SubmitError.configure({}),
      Editorid.configure({
         editorid: editorid,
      }),
   ];

   const doc = parseEditorContent(text);

   if (doc) {
      extensions.push(
         ValidateDoc.configure({
            doc,
            fallbackHandler: fallbackToPlaintext,
         })
      );
   }

   if (characterCountConfig) {
      extensions.push(
         CustomCharacterCount.configure({
            minCharacters: characterCountConfig.minCharacters,
            limit: characterCountConfig.maxCharacters,
         })
      );
   }

   if (submitHandler) {
      extensions.push(
         Submit.configure({
            submit: submitHandler,
         })
      );
   }

   return extensions;
};
